import React, { useState } from "react";
import { parsePhoneNumber } from "libphonenumber-js";
import {
  Checkbox,
  Error,
  Radio,
  GoogleAddress,
  Label,
  TextField,
  Select,
  TextFieldWithLabel,
  DateField,
} from "../input";
import { Formik, Form } from "formik";
import FormikErrorFocus from "formik-error-focus";

import moment from "moment";
import { EnrolmentValues, ResearchParticipantValues } from "./types";
import LoaderButton from "./LoaderButton";

import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import ResearchParticipant from "./EnrolmentFormResearchParticipant";

const hiddenIfFalse = (val: boolean) => {
  if (!val) {
    return "hidden";
  } else {
    return "";
  }
};

const recall2Years = "2_years";
const recall1Year = "1_year";
const recall6Months = "6_months";
const recallLess6Months = "less_6_months";

const emailAddressRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/gim;
const postcodeRegex = /^\d{4}$/gim;
const ndssRegex = /^\d{5,9}$/gim;
const hba1cRegex = /^\d{1,2}(\.\d{0,2})?$/gim;

type RequiredValues = Pick<
  EnrolmentValues,
  "title" | "first_name" | "surname" | "indigenous"
>;

type ResearchParticipantRequiredValues = Pick<
  EnrolmentValues,
  | "visual_acuity_right"
  | "visual_acuity_left"
  | "visual_acuity_right_normal_with_modifier"
  | "visual_acuity_left_normal_with_modifier"
  | "diabetic_retinopathy_right"
  | "diabetic_retinopathy_left"
  | "macular_oedema_right"
  | "macular_oedema_left"
>;

export type EnrolmentFormProps = {
  csrfToken: string;
  values: EnrolmentValues;
  saveEnrolmentForm: (values: EnrolmentValues) => void;
};

function EnrolmentForm({
  csrfToken,
  values: initialValues,
  saveEnrolmentForm,
}: EnrolmentFormProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleHba1cUnknownChange = (
    e: React.ChangeEvent<any>,
    values: EnrolmentValues,
    handleChange: (e: React.ChangeEvent<any>) => void,
  ) => {
    values.hba1c = "";
    handleChange(e);
  };

  const handleRecallChange = (
    e: React.ChangeEvent<any>,
    values: EnrolmentValues,
  ) => {
    values.recall_months = e.currentTarget.value;
  };

  const errorClass = (condition: string | boolean | undefined) => {
    if (!!condition) {
      return " field_with_errors";
    } else {
      return "";
    }
  };

  const validAustralianMobile = (number: string) => {
    try {
      const parsedNumber = parsePhoneNumber(number, "AU");
      return parsedNumber.isValid() && parsedNumber.getType() == "MOBILE";
    } catch (error) {
      return false;
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          let errors: Partial<Record<keyof EnrolmentValues, string>> = {};

          if (!values.title) {
            errors.title = "Please select a valid title";
          }

          if (!values.first_name) {
            errors.first_name = "Please enter a first name, e.g. Sam";
          }

          if (!values.surname) {
            errors.surname = "Please enter a surname, e.g. Smith";
          }

          if (!values.dob) {
            errors.dob = "Please enter a valid date, e.g. 11/04/1980";
          } else if (!moment(values.dob, "YYYY-MM-DD", true).isValid()) {
            errors.dob = "Please enter a valid date, e.g. 11/04/1980";
          } else if (
            moment(values.dob, "YYYY-MM-DD").diff(Date.now(), "days") >= 0
          ) {
            errors.dob = "Please enter a date before today";
          }

          if (!values.postcode.match(postcodeRegex)) {
            errors.postcode =
              "Please enter a 4 digit postcode number, e.g. 3000";
          }

          if (values.indigenous !== "yes" && values.indigenous !== "no") {
            errors.indigenous =
              "Please select if the patient is of Aboriginal or Torres Strait Islander origin";
          }

          if (values.ndss) {
            if (!values.ndss.match(ndssRegex)) {
              errors.ndss =
                "Please enter a 5-9 digit number for the NDSS membership";
            }
          }

          if (values.hba1c) {
            if (!values.hba1c.match(hba1cRegex)) {
              errors.hba1c =
                "Please enter a valid result for the hba1c test, or tick 'unknown' if the patient does not know their result";
            }
          }

          if (values.hba1c_unknown === false && values.hba1c === "") {
            errors.hba1c = "Please enter the results of the hba1c test";
          }

          if (values.follow_up_sms) {
            if (!values.follow_up_sms_number) {
              errors.follow_up_sms_number =
                "Phone number must be australian mobile, e.g. 0405 123 456";
            } else if (!validAustralianMobile(values.follow_up_sms_number)) {
              errors.follow_up_sms_number =
                "Phone number must be australian mobile, e.g. 0405 123 456";
            }
          }

          if (values.follow_up_email) {
            if (!values.follow_up_email_address) {
              errors.follow_up_email_address =
                "Please enter a valid email, e.g. user@example.com";
            } else if (
              !values.follow_up_email_address.match(emailAddressRegex)
            ) {
              errors.follow_up_email_address =
                "Please enter a valid email, e.g. user@example.com";
            }
          }

          if (values.follow_up_post) {
            if (!values.follow_up_post_address) {
              errors.follow_up_post_address =
                "A follow up postal address is required, e.g. 123 Street, Brunswick, Melbourne";
            }
          }

          if (
            !(
              values.follow_up_email ||
              values.follow_up_post ||
              values.follow_up_sms
            )
          ) {
            errors.follow_up_email = "Please enter a contact email address";
          }

          if (values.patient_consent === false) {
            errors.patient_consent =
              "Please ensure the patient has provided consent";
          }

          if (values.research_participant) {
            if (!values.visual_acuity_right) {
              errors.visual_acuity_right =
                "Please enter a value for the right eye visual acuity, e.g. 20";
            }

            if (!values.visual_acuity_right_normal_with_modifier) {
              errors.visual_acuity_right_normal_with_modifier =
                "Please enter a value for the right eye visual acuity with modifier, e.g. 20";
            }

            if (!values.visual_acuity_left) {
              errors.visual_acuity_left =
                "Please enter a value for the left eye visual acuity, e.g. 20";
            }

            if (!values.visual_acuity_left_normal_with_modifier) {
              errors.visual_acuity_left_normal_with_modifier =
                "Please enter a value for the left eye visual acuity with modifier, e.g. 20";
            }

            if (!values.diabetic_retinopathy_right) {
              errors.diabetic_retinopathy_right =
                "Please select the level of diabetic retinopathy for the right eye";
            }

            if (!values.diabetic_retinopathy_left) {
              errors.diabetic_retinopathy_left =
                "Please select the level of diabetic retinopathy for the left eye";
            }

            if (!values.macular_oedema_right) {
              errors.macular_oedema_right =
                "Please select the presence of macular oedema for the right eye";
            }

            if (!values.macular_oedema_left) {
              errors.macular_oedema_left =
                "Please select the presence of macular oedema for the left eye";
            }
          }

          return errors;
        }}
        onSubmit={(values) => {
          saveEnrolmentForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {
          const communicationError =
            (touched.follow_up_sms ||
              touched.follow_up_email ||
              touched.follow_up_post ||
              errors.follow_up_sms_number ||
              errors.follow_up_email_address ||
              errors.follow_up_post_address) &&
            !values.follow_up_sms_number &&
            !values.follow_up_email_address &&
            !values.follow_up_post_address
              ? "Please select the patients communication preference"
              : "";

          return (
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              data-testid="enrolment-form"
            >
              {/* 
            // @ts-ignore */}
              <FormikErrorFocus
                offset={-50}
                align={"top"}
                focusDelay={200}
                ease={"linear"}
                duration={250}
              />
              <div className="mdc-layout-grid max-width">
                <h1>Record a visit</h1>

                <div className="practice-details form-rows">
                  <div className="mdc-layout-grid__inner">
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div className="form-group" id="practice_name_form_group">
                        <Label name="practice_name">
                          Practice name - where you saw this patient (optional)
                        </Label>
                        <div className="message-text mb-2">
                          <span>
                            (If entered, this will be included on the KeepSight
                            recall that will be sent to the patient)
                          </span>
                        </div>
                        <TextField name="practice_name" />
                      </div>
                    </div>
                  </div>
                </div>

                <fieldset className="patient-details form-rows">
                  <legend className="sr-only">Patient details</legend>
                  <div className="mdc-layout-grid__inner" aria-hidden="true">
                    <div className="mdc-layout-grid__cell--span-7">
                      <div className="gradient">
                        <div className="gradient-text">Patient details</div>
                      </div>
                    </div>
                  </div>

                  <div className="mdc-layout-grid__inner">
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-2">
                      <div
                        className={
                          "form-group" +
                          errorClass(touched.title && errors.title)
                        }
                        data-testid="title_form_group"
                      >
                        <div className="mb-1">
                          <Label name="title">Title</Label>
                        </div>
                        <Select
                          name="title"
                          value={values.title}
                          autoComplete="honorific-prefix"
                        >
                          <option value={""} disabled></option>
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Miss">Miss</option>
                          <option value="Ms">Ms</option>
                          <option value="Dr">Dr</option>
                          <option value="Prof">Prof</option>
                          <option value="Rev">Rev</option>
                        </Select>
                      </div>
                    </div>

                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div data-testid="first_name_form_group">
                        <TextFieldWithLabel
                          autoComplete="given-name"
                          name="first_name"
                          label="First Name"
                        />
                      </div>
                    </div>

                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div data-testid="surname_form_group">
                        <TextFieldWithLabel
                          name="surname"
                          label="Surname"
                          autoComplete="family-name"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mdc-layout-grid__inner">
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-2">
                      <div data-testid="dob_form_group">
                        <div className="mb-1">
                          <Label name="dob">Date of Birth</Label>
                        </div>
                        <DateField name="dob" />
                      </div>
                    </div>
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-2">
                      <div data-testid="postcode_form_group">
                        <TextFieldWithLabel name="postcode" label="Postcode" />
                      </div>
                    </div>

                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div data-testid="indigenous_form_group">
                        <Label name="indigenous">
                          Aboriginal and Torres Strait Islander
                        </Label>

                        <div className="input-radio">
                          <div className="mdc-form-field">
                            <Radio
                              id="indigenous_yes"
                              name="indigenous"
                              label="Yes"
                              value="yes"
                              checked={values.indigenous === "yes"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy="indigenous_description"
                            />
                          </div>

                          <div className="mdc-form-field">
                            <Radio
                              id="indigenous_no"
                              name="indigenous"
                              label="No"
                              value="no"
                              checked={values.indigenous === "no"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy="indigenous_description"
                            />
                          </div>

                          {touched.indigenous && errors.indigenous && (
                            <Error
                              id="indigenousErrorDescription"
                              errorText={errors.indigenous}
                            />
                          )}

                          <p className="mt-4" id="indigenous_description">
                            We ask this question as Aboriginal and Torres Strait
                            Islander people are at greater risk of developing
                            diabetes and eye complications than non-Indigenous
                            people.
                            <br />
                            <br />
                            Eye checks are recommended each year for Aboriginal
                            and Torres Straight Islander people.
                            <br />
                            <br />
                            So, this will influence when the eye check reminder
                            will be sent.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mdc-layout-grid__inner">
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div data-testid="ndss_form_group">
                        <div className="mb-1">
                          <label htmlFor="ndss">NDSS</label>
                        </div>
                        <TextField
                          name="ndss"
                          ariaDescribedBy="ndss_description"
                        />
                        <p className="mt-4" id="ndss_description">
                          This is the patient’s eight digit National Diabetes
                          Service Scheme (NDSS) registration number.
                          <br />
                          <br />
                          It can be found on the front of their NDSS card, or
                          they can call the NDSS on 1800 637 700.
                        </p>
                      </div>
                    </div>

                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4">
                      <div data-testid="hba1c_form_group">
                        <div className="mb-1">
                          <label htmlFor="hba1c">HbA1c</label>
                        </div>

                        <TextField
                          name="hba1c"
                          ariaDescribedBy="hba1c_description"
                        />
                        <Checkbox
                          id="hba1c_unknown"
                          label="Unknown"
                          value={values.hba1c_unknown}
                          defaultChecked={values.hba1c_unknown === true}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleHba1cUnknownChange(e, values, handleChange)
                          }
                          onBlur={handleBlur}
                          ariaDescribedBy="hba1c_description"
                        />
                        <p className="mt-4" id="hba1c_description">
                          The HbA1c test shows an average blood glucose level
                          over the past 10–12 weeks and should be arranged by
                          their doctor every 3–6 months
                          <br />
                          <br />
                          See Blood glucose monitoring for more information.
                        </p>
                      </div>
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="sr-only">Last eye test?</legend>
                  <div className="last-eye-test form-rows" aria-hidden="true">
                    <div className="mdc-layout-grid__inner">
                      <div className="mdc-layout-grid__cell--span-7">
                        <div className="gradient">
                          <div className="gradient-text">Last eye test?</div>
                        </div>
                      </div>
                    </div>
                    <div className="mdc-layout-grid__inner">
                      <div className="mdc-layout-grid__cell--span-12">
                        <div
                          className={
                            "form-group" +
                            errorClass(
                              touched.last_eye_test && errors.last_eye_test,
                            )
                          }
                          id="last_eye_test_form_group"
                        >
                          <label>
                            When was their last eye test? (optional)
                          </label>
                          <br />
                          <div className="input-radio">
                            <div className="mdc-form-field">
                              <Radio
                                id="last_eye_test_less_6"
                                name="last_eye_test"
                                label="less than 6 months ago"
                                value="less_6_months"
                                checked={
                                  values.last_eye_test === "less_6_months"
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <br />
                            <div className="mdc-form-field">
                              <Radio
                                id="last_eye_test_6_12"
                                name="last_eye_test"
                                label="6-12 months"
                                value="6_12_months"
                                checked={values.last_eye_test === "6_12_months"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <br />
                            <div className="mdc-form-field">
                              <Radio
                                id="last_eye_test_1_2"
                                name="last_eye_test"
                                label="1-2 years"
                                value="1_2_years"
                                checked={values.last_eye_test === "1_2_years"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <br />
                            <div className="mdc-form-field">
                              <Radio
                                id="last_eye_test_over_2"
                                name="last_eye_test"
                                label="over 2 years ago"
                                value="over_2_years"
                                checked={
                                  values.last_eye_test === "over_2_years"
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <br />
                            <div className="mdc-form-field">
                              <Radio
                                id="last_eye_test_unknown_never"
                                name="last_eye_test"
                                label="don't know / never"
                                value="unknown_never"
                                checked={
                                  values.last_eye_test === "unknown_never"
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <br />
                          </div>
                        </div>
                        {touched.last_eye_test && errors.last_eye_test && (
                          <Error
                            id="last_eye_testErrorDescription"
                            errorText={errors.last_eye_test}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="sr-only">KeepSight recall</legend>
                  <div
                    className="keepsight-recall form-rows"
                    aria-hidden="true"
                  >
                    <div className="mdc-layout-grid__inner">
                      <div className="mdc-layout-grid__cell--span-7">
                        <div className="gradient">
                          <div className="gradient-text">KeepSight recall</div>
                        </div>
                      </div>
                    </div>
                    <p className="mt-4">
                      When would you like your patient to receive their
                      KeepSight recall notice for their next eye check
                      appointment?
                    </p>
                    <label>Recall period</label>
                    <div
                      className="mdc-layout-grid__inner"
                      data-testid="recall-periods"
                    >
                      <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12">
                        <div className="mdc-form-field">
                          <Radio
                            id="recall_two_years"
                            name="recall_two_years"
                            label="2 years"
                            checked={values.recall_months === recall2Years}
                            value={recall2Years}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              handleRecallChange(e, values);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        <br />
                        <div className="mdc-form-field">
                          <Radio
                            id="recall_one_year"
                            name="recall_one_year"
                            label="1 year"
                            checked={values.recall_months === recall1Year}
                            value={recall1Year}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              handleRecallChange(e, values);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        <br />
                        <div className="mdc-form-field">
                          <Radio
                            id="recall_six_months"
                            name="recall_six_months"
                            label="6 months"
                            checked={values.recall_months === recall6Months}
                            value={recall6Months}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              handleRecallChange(e, values);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        <br />
                        <div className="mdc-form-field">
                          <Radio
                            id="recall_less_6_months"
                            name="recall_less_6_months"
                            label="less than 6 months"
                            checked={values.recall_months === recallLess6Months}
                            value={recallLess6Months}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              handleRecallChange(e, values);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="sr-only">Communication preferences</legend>
                  <div
                    className="follow-up-preference form-rows"
                    aria-hidden="true"
                  >
                    <div className="mdc-layout-grid__inner">
                      <div className="mdc-layout-grid__cell--span-7">
                        <div className="gradient">
                          <div className="gradient-text">
                            Communication preferences
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mdc-layout-grid__inner">
                      <fieldset
                        className="mdc-layout-grid__cell mdc-layout-grid__cell--span-4"
                        aira-label={
                          communicationError
                            ? `How to contact hte patient? Error - ${communicationError}`
                            : "How to contact the patient?"
                        }
                      >
                        <label>
                          How would the patient like to be contacted?
                        </label>
                        <Checkbox
                          id="follow_up_sms"
                          name="follow_up_sms"
                          label="SMS"
                          value={values.follow_up_sms}
                          defaultChecked={values.follow_up_sms === true}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <br />
                        <div
                          data-testid="follow_up_sms_number_form_group"
                          className={
                            hiddenIfFalse(values.follow_up_sms) + " form-group"
                          }
                        >
                          <TextFieldWithLabel
                            name="follow_up_sms_number"
                            label="Australian SMS Number"
                          />
                        </div>
                        <Checkbox
                          id="follow_up_email"
                          name="follow_up_email"
                          label="Email"
                          value={values.follow_up_email}
                          defaultChecked={values.follow_up_email === true}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <br />
                        <div
                          data-testid="follow_up_email_address_form_group"
                          className={
                            hiddenIfFalse(values.follow_up_email) +
                            " form-group"
                          }
                        >
                          <TextFieldWithLabel
                            name="follow_up_email_address"
                            label="Please provide the patients email address"
                          />
                        </div>
                        <Checkbox
                          id="follow_up_post"
                          name="follow_up_post"
                          label="Post"
                          value={values.follow_up_post}
                          defaultChecked={values.follow_up_post}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <br />
                        <div
                          data-testid="follow_up_post_address_form_group"
                          className={
                            hiddenIfFalse(values.follow_up_post) + " form-group"
                          }
                        >
                          <label
                            className="address-label"
                            htmlFor="follow_up_post_address"
                          ></label>
                          <br />
                          <GoogleAddress
                            name="follow_up_post_address"
                            id="follow_up_post_address"
                            onChange={(address: string) => {
                              handleChange(address);
                              setFieldValue("follow_up_post_address", address);
                            }}
                            onBlur={handleBlur}
                            className="input-text"
                          />
                          {touched.follow_up_post_address &&
                            errors.follow_up_post_address && (
                              <Error
                                id="follow_up_post_addressErrorDescription"
                                errorText={errors.follow_up_post_address}
                              />
                            )}
                        </div>
                      </fieldset>
                      {communicationError && (
                        <div id="communication_errors">
                          <Error
                            id="communicationsMissingError"
                            errorText={communicationError}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="sr-only">KeepSight consent</legend>
                  <div className="keepsight-consent form-rows">
                    <div className="mdc-layout-grid__inner" aria-hidden="true">
                      <div className="mdc-layout-grid__cell--span-7">
                        <div className="gradient">
                          <div className="gradient-text">KeepSight consent</div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="mdc-layout-grid__inner"
                      data-testid="patient_consent"
                    >
                      <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12">
                        <div className="mdc-form-field">
                          <Checkbox
                            id="patient_consent"
                            name="patient_consent"
                            label="I confirm the patient has provided consent to enrol"
                            value={values.patient_consent}
                            defaultChecked={values.patient_consent}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>
                        {touched.patient_consent && errors.patient_consent && (
                          <Error
                            id="patient_consentErrorDescription"
                            errorText={errors.patient_consent}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </fieldset>

                <ResearchParticipant
                  csrfToken={csrfToken}
                  setFieldValue={setFieldValue}
                  values={values as ResearchParticipantValues}
                  setIsUploading={setIsUploading}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                />

                <div className="submit-button-group">
                  <div className="mdc-layout-grid__inner">
                    <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
                      <LoaderButton
                        id="review_enrolment_btn"
                        variant="primary"
                        className="primary-button"
                        size="lg"
                        type="submit"
                        isLoading={isUploading || isSubmitting}
                        loadingText="Uploading..."
                        text="Submit"
                        aria-label="submit"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default EnrolmentForm;
